import { useTranslation } from "react-i18next";
import UserProfilePictureAvatar from "../../../../components/avatar/UserProfilePictureAvatar";
import SessionModel, { statuses } from "../../../../api/models/SessionModel";
import moment from "moment";
import SessionInfoModal from "../../../../components/modal/SessionInfoModal";
import { useEffect, useState } from "react";
import { EyeIcon } from "@heroicons/react/outline";
import { setAsSeenSession } from "../../../../api/endpoints/supervisors/sessions";
import { useNavigate } from "react-router-dom";
import DropDownSearchFilter from "../../../../components/dropdowns/DropDownSearchFilter";
import { indexStudents } from "../../../../api/endpoints/supervisors/students";
import StudentModel from "../../../../api/models/StudentModel";
import { indexTutors } from "../../../../api/endpoints/supervisors/tutors";
import DropDownFilter from "../../../../components/dropdowns/DropDownFilter";

type SessionsTableProps = {
  sessions: SessionModel[];
  refetch: any;
  page: number;
  setStudentFilter: any;
  setTutorFilter: any;
  setStatusFilter: any;
  statusFilter: string;
  studentFilter: number[];
  tutorFilter: number[];
};
export const SessionsTable = (props: SessionsTableProps) => {
  const {
    sessions,
    refetch,
    page,
    setStudentFilter,
    setTutorFilter,
    setStatusFilter,
    statusFilter,
    studentFilter,
    tutorFilter,
  } = props;
  const LocalBasePath = "page.supervisor.sessions";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const [open, setOpen] = useState<boolean>(false);
  const [students, setStudents] = useState<StudentModel[]>([]);
  const [tutors, setTutors] = useState<StudentModel[]>([]);
  const navigate = useNavigate();
  const [reasonDetails, setReasonDetails] = useState<{
    reason: string;
    date: string;
  }>({
    date: "",
    reason: "",
  });

  useEffect(() => {
    getAllStudents();
    getAllTutors();
  }, []);

  const changeSessionViewed = async (id: string | number) => {
    if (!!id) {
      const response = await setAsSeenSession({
        viewed_sessions_ids: [id],
      });
      if (!!response.isSuccess) {
        refetch();
      }
    }
  };

  const getAllStudents = async () => {
    let { data } = await indexStudents();
    setStudents(data);
  };

  const getAllTutors = async () => {
    let { data } = await indexTutors();
    setTutors(data);
  };

  const onSessionClick = (session: SessionModel) => {
    if (!!session.cancellation) {
      setReasonDetails({
        reason: session.cancellation ?? "",
        date: moment(session.start_time).fromNow(true),
      });
      setOpen(true);
      changeSessionViewed(session?.id);
    } else if (session?.isViewableBySupervisor) {
      changeSessionViewed(session.id);
      navigate(`/supervisor/dashboard/sessions/?page=${page}&&tab=1`);
      navigate(`/supervisor/dashboard/sessions/${session.id}/?page=${page}`);
    }
  };

  return (
    <div className="">
      <table className="min-w-full border-y table-fixed border-gray-200">
        <thead className="bg-white ">
          <tr className="">
            <th
              scope="col"
              className=" px-3  py-3.5 text-justify text-sm font-semibold text-gray-500"
            >
              {t("session")}
            </th>
            <th
              scope="col"
              className=" px-3  py-3.5 text-justify   hidden sm:table-cell text-sm font-semibold text-gray-500 "
            >
              <div className="flex items-center">
                {t("reader")}
                <DropDownSearchFilter
                  list={students}
                  setStudentTutorFilter={setStudentFilter}
                  selectedIds={studentFilter}
                />
              </div>
            </th>
            <th
              scope="col"
              className="py-3.5 ps-4 pe-3  text-justify text-sm font-semibold text-gray-500 sm:ps-6 hidden md:table-cell"
            >
              <div className="flex items-center">
                {t("tutor")}
                <DropDownSearchFilter
                  list={tutors}
                  setStudentTutorFilter={setTutorFilter}
                  selectedIds={tutorFilter}
                />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-justify text-sm font-semibold text-gray-500 hidden md:table-cell"
            >
              {t("sessionTime")}
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-justify text-sm font-semibold text-gray-500 "
            >
              <div className="flex items-center">
                {t("sessionStatus")}
                <DropDownFilter
                  list={statuses}
                  setStatusFilter={setStatusFilter}
                  statusFilter={statusFilter}
                />
              </div>
            </th>
            <th scope="col" className="py-3.5 ps-3 pe-4 sm:pe-6">
              <span className="sr-only">More options</span>
            </th>
          </tr>
        </thead>
        <tbody data-cy="studentsTable">
          <>
            {sessions.map((session, index) => {
              return (
                <tr
                  key={`SessionInfoTable_${index}_${session.id}`}
                  className={` border-t ${
                    session?.isClickableBySupervisor && "cursor-pointer"
                  } hover:bg-gray-50 `}
                  onClick={() => onSessionClick(session)}
                >
                  <td className="px-3 py-3.5">
                    <div className="flex gap-3 pe-4">
                      <div className="flex items-start gap-1 flex-col">
                        <h3 className="text-sm  text-gray-900">
                          {"# " + session?.sessionNumber}
                        </h3>
                        <p className="text-xs font-normal sm:hidden text-gray-500">
                          {session?.student?.full_name ??
                            `${session?.student?.firstName} ${session?.student?.lastName}`}
                        </p>
                        <p className="text-xs font-normal text-gray-500">
                          {moment(session?.created_at)?.format("DD/MM/YYYY")}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3  hidden sm:table-cell py-3.5">
                    <div className="flex gap-3 pe-4 ">
                      <UserProfilePictureAvatar
                        profilePictureUrl={
                          session?.student?.profile_picture_url
                        }
                        className="w-10 h-10"
                        alt="sand-user-img"
                      />
                      <div className="flex items-center  ">
                        <h3 className="text-sm  text-[#6B7280]">
                          {session?.student?.full_name ??
                            `${session?.student?.firstName} ${session?.student?.lastName}`}
                        </h3>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 hidden sm:table-cell py-3.5">
                    <div className="flex gap-3 pe-4 ">
                      <UserProfilePictureAvatar
                        profilePictureUrl={session?.tutor?.profile_picture_url}
                        className="w-10 h-10"
                        alt="sand-tutor-img"
                      />
                      <div className="flex items-center  ">
                        <h3 className="text-sm  text-[#6B7280]">
                          {session?.tutor?.full_name ??
                            `${session?.tutor?.firstName} ${session?.tutor?.lastName}`}
                        </h3>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3.5 text-sm text-gray-500 hidden md:table-cell ">
                    {moment(session?.start_time).format("HH:mm A")}
                  </td>

                  <td className="px-3 py-3.5    ">
                    {session?.status === "completed" ? (
                      <span className="text-green-600  justify-center flex items-center flex-nowrap   text-sm font-medium py-0.5 px-2.5 bg-green-50 rounded-[10px]">
                        {t("done")}
                      </span>
                    ) : session?.status === "terminated_early" ? (
                      <span className="text-yellow-500 flex justify-center items-center flex-nowrap   text-sm font-medium py-0.5 px-2.5 bg-[#FEFCE8] rounded-[10px]">
                        {t(session?.status)}
                      </span>
                    ) : (
                      <span className="text-red-600 flex justify-center items-center flex-nowrap   text-sm font-medium py-0.5 px-2.5 bg-red-50 rounded-[10px]">
                        {t(session?.status)}
                      </span>
                    )}
                  </td>
                  <td className=" px-3 py-3.5 text-sm text-gray-500">
                    <div className="flex justify-end">
                      {session?.viewed ? (
                        <EyeIcon
                          className="h-4 w-4 text-gray-500 "
                          aria-hidden="true"
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </>
        </tbody>
      </table>
      <SessionInfoModal
        reason={reasonDetails}
        open={open}
        setOpen={() => setOpen(false)}
      />
    </div>
  );
};
